/**
 * PeopleTab — the People pane of the `/admin` portal.
 *
 * Composition mirrors OrganizationsTab (Carbon, mockup 02):
 *   1. A "People" DataTable whose status tabs (FilterChipRow: All / Active /
 *      Not yet welcomed / Inactive) own the filter key; named-subject voice
 *      on the pending tab (not "PendingWelcome").
 *   2. The cross-pivot filter tag: when arriving from Organizations with an
 *      `orgFilter` param, we narrow the table and show a dismissible
 *      "Organization: {name}" tag in the toolbar so the user knows the list
 *      is filtered.
 *   3. Columns: Name (email beneath), Role, Organization (PivotChip to the
 *      Organizations tab), Tenants (count), Welcome status, Open. A row
 *      click or Enter also opens the drawer.
 *   4. AdminDrawer + PersonDrawerContent: opens when `state.drawer`
 *      matches `person:<objectId>`. Drawer body owns its own action flows
 *      and reaches back through this component for mutation handles.
 *
 * No native <select> renders on this pane: the person drawer's organization
 * picker must be the first select in the document (the harness's
 * `admin-person-drawer-org` hook picks it with `locator('select').first()`).
 *
 * State ownership:
 *   - Tab-local filter, drawer id, and cross-tab pivots live in the URL
 *     via `useAdminPortalState`. Back button + deep links work for free.
 *   - Data + mutations come from `useUsers`. We also call `useOrganizations`
 *     to feed the org-picker in PersonDrawerContent — TanStack cache means
 *     the OrganizationsTab + AdminPortal score box share the same fetch.
 *   - No `<h1>` — the breadcrumb carries "Administration · Admin Portal".
 *
 * Voice:
 *   - Status tab "Not yet welcomed": named state, not "PendingWelcome".
 *   - The empty row is one sentence that names the way out.
 *   - Welcome status when null is "Not yet welcomed" in helper text.
 */

import { useEffect, useMemo, useRef } from 'react'

import { useUsers, isHalfGrantedAdmin, type UserDto } from '@/hooks/useUsers'
import { useOrganizations } from '@/hooks/useOrganizations'
import { useTenants } from '@/hooks/useTenants'
import { useAdminPortalState } from '@/hooks/useAdminPortalState'

import { Button } from '@/components/carbon/Button'
import { DataTable, type DataColumn } from '@/components/carbon/DataTable'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { Tag } from '@/components/carbon/Tag'
import FilterChipRow, { type FilterChip } from '@/components/AdminPortal/FilterChipRow'
import PivotChip from '@/components/AdminPortal/PivotChip'
import AdminDrawer from '@/components/AdminPortal/AdminDrawer'
import PersonDrawerContent from '@/components/AdminPortal/PersonDrawerContent'
import PlatformAdminBadge from '@/components/UserManagement/PlatformAdminBadge'

/* -------------------------------------------------------------------------- */
/* Filter helpers                                                             */
/* -------------------------------------------------------------------------- */

type FilterKey = 'all' | 'active' | 'pending' | 'inactive'

const VALID_FILTERS: ReadonlySet<string> = new Set(['all', 'active', 'pending', 'inactive'])

function parseFilter(raw: string | null): FilterKey {
  return raw && VALID_FILTERS.has(raw) ? (raw as FilterKey) : 'all'
}

function matchesFilter(u: UserDto, key: FilterKey): boolean {
  if (key === 'all') return true
  if (key === 'active') return u.isActive === true
  // "Not yet welcomed" mirrors `deriveUserPendingState` exactly:
  // null counts, undefined does not (unknown ≠ pending).
  if (key === 'pending') return u.welcomeEmailSentAt === null
  return u.isActive === false
}

/* -------------------------------------------------------------------------- */
/* Drawer id helpers                                                          */
/* -------------------------------------------------------------------------- */

const PERSON_DRAWER_PREFIX = 'person:'

function parsePersonDrawer(drawer: string | null): string | null {
  if (!drawer || !drawer.startsWith(PERSON_DRAWER_PREFIX)) return null
  return drawer.slice(PERSON_DRAWER_PREFIX.length)
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function PeopleTab() {
  const state = useAdminPortalState()
  const {
    users,
    isLoading,
    isError,
    error,
    pendingCount,
    updateUserRole,
    updateUserOrganization,
    assignUserToTenant,
    removeUserFromTenant,
    deactivateUser,
    sendWelcomeEmail,
    setPlatformAdmin,
  } = useUsers()

  // Pull the org list to feed PersonDrawerContent's reassign picker. Shares
  // a TanStack cache entry with OrganizationsTab / AdminPortal score box
  // (same hook + same `{ pageSize: 200 }` key) — no extra round-trip.
  const orgs = useOrganizations({ pageSize: 200 })
  const availableOrgs = orgs.data?.items

  // Tenant list feeds PersonDrawerContent's Manage-tenant-access editor.
  // Shares a TanStack cache entry with MonitoredTenantsTab / AdminPortal
  // score box, so opening the drawer doesn't trigger a second round-trip.
  const tenants = useTenants()
  const availableTenants = tenants.tenants

  /* ── Filter + pivot ──────────────────────────────────────────────────── */

  const activeFilter = parseFilter(state.filter)

  const counts = useMemo(() => ({
    all: users.length,
    active: users.filter(u => u.isActive === true).length,
    // pendingCount is the canonical derivation from the hook — same value
    // the nav badge and the AdminPortal score box use.
    pending: pendingCount,
    inactive: users.filter(u => u.isActive === false).length,
  }), [users, pendingCount])

  // Drawer targets a row by objectId — keep the lookup against the FULL
  // list, not the filtered subset, so the drawer survives filter toggles
  // (mirrors OrganizationsTab's lastOrgRef pattern).
  const filteredUsers = useMemo(() => {
    const byFilter = users.filter(u => matchesFilter(u, activeFilter))
    if (!state.orgFilter) return byFilter
    return byFilter.filter(u => u.organizationId === state.orgFilter)
  }, [users, activeFilter, state.orgFilter])

  const chips: FilterChip[] = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'active', label: 'Active', count: counts.active },
    { key: 'pending', label: 'Not yet welcomed', count: counts.pending },
    { key: 'inactive', label: 'Inactive', count: counts.inactive },
  ]

  /* ── Drawer resolution ───────────────────────────────────────────────── */

  const drawerObjectId = parsePersonDrawer(state.drawer)
  const drawerUser = useMemo(
    () => (drawerObjectId ? users.find(u => u.objectId === drawerObjectId) ?? null : null),
    [users, drawerObjectId],
  )

  /* ── Render ──────────────────────────────────────────────────────────── */

  const filterLabel = chips.find(c => c.key === activeFilter)?.label.toLowerCase() ?? 'all'
  const orgFilterName = state.orgFilter
    ? availableOrgs?.find(o => o.id === state.orgFilter)?.name ?? 'Selected organization'
    : null
  const statusWord = activeFilter === 'all' ? '' : `${filterLabel} `

  return (
    <div className="flex flex-col gap-8">
      {isError && (
        <InlineNotification kind="error" title="People did not load.">
          {error?.message ?? 'Reload the page to try again.'}
        </InlineNotification>
      )}

      <PeopleTable
        rows={filteredUsers}
        isLoading={isLoading && users.length === 0}
        tabs={
          <FilterChipRow
            chips={chips}
            active={activeFilter}
            onChange={key => state.setFilter(key === 'all' ? null : key)}
            ariaLabel="Filter people by status"
          />
        }
        toolbar={
          // Cross-tab pivot filter, only when scoped to one org. A Carbon
          // filter tag: it names the scope and dismisses to clear it.
          orgFilterName ? (
            <div className="flex min-h-12 items-center px-4">
              <Tag
                onDismiss={() => state.setOrgFilter(null)}
                dismissLabel="Clear the organization filter"
              >
                {`Organization: ${orgFilterName}`}
              </Tag>
            </div>
          ) : undefined
        }
        empty={
          users.length === 0
            ? isError
              ? 'The list did not load.'
              : 'No one has access yet. Invite the first admin from an organization.'
            : orgFilterName
              ? `No ${statusWord}people in ${orgFilterName}. Clear the organization filter to see everyone.`
              : `No ${statusWord}people. Choose another status to see the rest.`
        }
        onOpen={u => state.setDrawer(`${PERSON_DRAWER_PREFIX}${u.objectId}`)}
        onPivotOrg={orgId =>
          state.mergePivot({ tab: 'organizations', orgFilter: orgId, filter: null })
        }
      />

      <PersonDrawer
        user={drawerUser}
        onClose={() => state.setDrawer(null)}
        onPivot={(_target, orgId) =>
          state.mergePivot({ tab: 'organizations', orgFilter: orgId, filter: null })
        }
        availableOrgs={availableOrgs}
        availableTenants={availableTenants}
        updateUserRole={updateUserRole}
        updateUserOrganization={updateUserOrganization}
        assignUserToTenant={assignUserToTenant}
        removeUserFromTenant={removeUserFromTenant}
        deactivateUser={deactivateUser}
        sendWelcomeEmail={sendWelcomeEmail}
        setPlatformAdmin={setPlatformAdmin}
      />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Subcomponents                                                              */
/* -------------------------------------------------------------------------- */

interface PeopleTableProps {
  rows: UserDto[]
  onOpen(u: UserDto): void
  onPivotOrg(orgId: string): void
  tabs?: React.ReactNode
  toolbar?: React.ReactNode
  isLoading?: boolean
  empty?: React.ReactNode
}

function PeopleTable({ rows, onOpen, onPivotOrg, tabs, toolbar, isLoading, empty }: PeopleTableProps) {
  const columns: DataColumn<UserDto>[] = [
    {
      id: 'name',
      header: 'Name',
      primary: true,
      cell: u => (
        <>
          <span className="flex flex-wrap items-center gap-2">
            <b className="font-semibold text-text-primary">{u.displayName || u.email}</b>
            {u.isPlatformAdmin && <PlatformAdminBadge isPlatformAdmin />}
            {isHalfGrantedAdmin(u) && <HalfGrantBadge />}
          </span>
          <small className="mt-0.5 block text-helper text-text-helper [overflow-wrap:anywhere]">{u.email}</small>
        </>
      ),
    },
    { id: 'role', header: 'Role', inlineOnPhone: true, cell: u => roleLabel(u.role) },
    {
      id: 'org',
      header: 'Organization',
      cell: u =>
        u.organizationId && u.organizationName ? (
          <PivotChip
            label={u.organizationName}
            onClick={() => onPivotOrg(u.organizationId as string)}
            ariaLabel={`View ${u.organizationName} in Organizations`}
          />
        ) : (
          <span className="text-text-helper">No organization</span>
        ),
    },
    {
      // Tenants: the count only. The drawer owns the assign/remove editor;
      // the row's Open is the way there.
      id: 'tenants',
      header: 'Tenants',
      inlineOnPhone: true,
      cell: u => <TenantsCount count={u.accessibleTenantCount} />,
    },
    {
      id: 'welcome',
      header: 'Welcome',
      inlineOnPhone: true,
      cell: u => {
        const sent = formatDate(u.welcomeEmailSentAt)
        return sent ? `Sent ${sent}` : <span className="text-text-helper">Not yet welcomed</span>
      },
    },
    {
      id: 'open',
      header: <span className="sr-only">Open</span>,
      align: 'end',
      cell: u => (
        <Button kind="ghost" size="sm" onClick={() => onOpen(u)} aria-label={`Open ${u.displayName || u.email}`}>
          Open
        </Button>
      ),
    },
  ]

  return (
    <DataTable
      title="People"
      description="Everyone with Guardian access, their role, and the organization they sign in from."
      columns={columns}
      rows={rows}
      rowId={u => u.objectId}
      rowLabel={u => u.displayName || u.email}
      tabs={tabs}
      toolbar={toolbar}
      isLoading={isLoading}
      onRowActivate={onOpen}
      empty={empty}
    />
  )
}

/**
 * Half-grant flag, shown next to a person who holds Global Admin but is not a
 * platform admin (the "GlobalAdmin who sees nothing" shape). A neutral
 * outline tag, not a hue: the state is recoverable and the drawer carries the
 * full explanation + the one-click fix. The advisor voice lives in the
 * tooltip and the drawer notice.
 */
function HalfGrantBadge() {
  return (
    <Tag
      kind="outline"
      size="sm"
      title="Global Admin without the platform grant. They may see no tenant data until the grant is finished. Open the row to finish or change it."
    >
      Half-granted
    </Tag>
  )
}

function TenantsCount({ count }: { count: number }) {
  return (
    <span
      className="tabular-nums"
      title={
        count === 0
          ? 'No tenants assigned. Open the row to add one.'
          : `${count} tenant${count === 1 ? '' : 's'} assigned. Open the row to edit.`
      }
    >
      {count === 0 ? <span className="text-text-helper">None</span> : count}
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* Drawer wrapper                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Wraps AdminDrawer + PersonDrawerContent and keeps the last user painted
 * through the 300ms slide-out (same trick as OrganizationsTab's
 * OrganizationDrawer). `isOpen` drives the transition; the captured ref
 * keeps the body rendered while the URL has already cleared.
 */
function PersonDrawer({
  user,
  onClose,
  onPivot,
  availableOrgs,
  availableTenants,
  updateUserRole,
  updateUserOrganization,
  assignUserToTenant,
  removeUserFromTenant,
  deactivateUser,
  sendWelcomeEmail,
  setPlatformAdmin,
}: {
  user: UserDto | null
  onClose(): void
  onPivot: React.ComponentProps<typeof PersonDrawerContent>['onPivot']
  availableOrgs: React.ComponentProps<typeof PersonDrawerContent>['availableOrgs']
  availableTenants: React.ComponentProps<typeof PersonDrawerContent>['availableTenants']
  updateUserRole: React.ComponentProps<typeof PersonDrawerContent>['updateUserRole']
  updateUserOrganization: React.ComponentProps<typeof PersonDrawerContent>['updateUserOrganization']
  assignUserToTenant: React.ComponentProps<typeof PersonDrawerContent>['assignUserToTenant']
  removeUserFromTenant: React.ComponentProps<typeof PersonDrawerContent>['removeUserFromTenant']
  deactivateUser: React.ComponentProps<typeof PersonDrawerContent>['deactivateUser']
  sendWelcomeEmail: React.ComponentProps<typeof PersonDrawerContent>['sendWelcomeEmail']
  setPlatformAdmin: React.ComponentProps<typeof PersonDrawerContent>['setPlatformAdmin']
}) {
  const lastUserRef = useRef<UserDto | null>(null)
  useEffect(() => {
    if (user) lastUserRef.current = user
  }, [user])

  const displayUser = user ?? lastUserRef.current
  if (!displayUser) return null

  const title = displayUser.displayName || displayUser.email
  const subtitle = `Person · ${displayUser.email}`

  return (
    <AdminDrawer
      isOpen={user !== null}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
    >
      <PersonDrawerContent
        user={displayUser}
        onClose={onClose}
        onPivot={onPivot}
        availableOrgs={availableOrgs}
        availableTenants={availableTenants}
        updateUserRole={updateUserRole}
        updateUserOrganization={updateUserOrganization}
        assignUserToTenant={assignUserToTenant}
        removeUserFromTenant={removeUserFromTenant}
        deactivateUser={deactivateUser}
        sendWelcomeEmail={sendWelcomeEmail}
        setPlatformAdmin={setPlatformAdmin}
      />
    </AdminDrawer>
  )
}

/* -------------------------------------------------------------------------- */
/* Pure helpers                                                               */
/* -------------------------------------------------------------------------- */

// Kept in sync with PersonDrawerContent's role labels — the same five roles
// shown in the drawer's role-change select. Hard-coded here rather than
// imported because the source list lives inside the drawer module as a
// component-local constant; mirroring three short strings is cheaper than
// reaching across module boundaries for a stable enum.
const ROLE_LABELS: Record<string, string> = {
  Viewer: 'Viewer',
  Approver: 'Approver',
  ComplianceOfficer: 'Compliance Officer',
  SecurityAdmin: 'Security Admin',
  GlobalAdmin: 'Global Admin',
}

function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role
}

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}
